import { useQuery, type DocumentNode } from '@apollo/client';
import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { ChevronRight, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ProfileField } from './types';

interface DictionaryPage { items: { value: string; label: string }[]; hasMore: boolean; next: number }
const validPage = (value: unknown, after: number): value is DictionaryPage => {
  if (!value || typeof value !== 'object') return false;
  const page = value as Partial<DictionaryPage>;
  return Array.isArray(page.items) && page.items.length <= 25
    && page.items.every(item => item && typeof item.value === 'string' && item.value && typeof item.label === 'string')
    && typeof page.hasMore === 'boolean' && typeof page.next === 'number' && Number.isSafeInteger(page.next)
    && page.next >= after && page.next <= 2147483647 && (!page.hasMore || page.next > after);
};

export const ProfileDictionaryField = ({ odrnumber, entity, field, query, form, disabled, inputName = field.name }: {
  odrnumber: number; entity: string; field: ProfileField; query: DocumentNode;
  form: UseFormReturn<Record<string, string>>; disabled: boolean; inputName?: string;
}) => {
  const id = `profile-${entity}-${field.name}`;
  const [text, setText] = useState('');
  const [request, setRequest] = useState<{ search: string; after: number } | null>(null);
  const { data, loading, error, refetch } = useQuery<{ dictionary?: unknown }>(query, {
    variables: { odrnumber, entity, field: field.name, search: request?.search ?? '', after: request?.after ?? 0 },
    skip: request === null, fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true,
  });
  const current = request !== null && request.search === text.trim();
  const dictionary = data?.dictionary;
  const page = current && !loading && !error && validPage(dictionary, request.after) ? dictionary : null;
  const searchable = text.trim().length >= 2 && text.trim().length <= 80 && text.trim().split(/\s+/).length <= 8;
  const find = () => {
    if (current && request.after === 0) { void refetch().catch(() => undefined); return; }
    setRequest({ search: text.trim(), after: 0 });
  };
  return <div className="space-y-2">
    <Label htmlFor={`${id}-input`}>{field.label}</Label>
    <Input id={`${id}-input`} className="h-8" disabled={disabled} {...form.register(inputName, { required: field.required })} />
    <Label htmlFor={`${id}-dictionary-search`}>Search {field.label.toLowerCase()}</Label>
    <div className="flex gap-2">
      <Input id={`${id}-dictionary-search`} className="h-8" value={text} maxLength={80} disabled={disabled}
        onChange={event => { setText(event.target.value); setRequest(null); }} />
      <Button id={`${id}-dictionary-find`} type="button" variant="outline" disabled={disabled || !searchable || (current && loading)}
        onClick={find}><Search aria-hidden="true" className="mr-2 h-4 w-4" />Search</Button>
    </div>
    {current && loading && <p role="status">Searching choices…</p>}
    {current && !loading && (error || !page) && <p role="alert" className="text-destructive">Unable to load choices. Search again to retry.</p>}
    {page && !page.items.length && <p>No choices match this search.</p>}
    {page && <div className="space-y-1">{page.items.map((option, index) => <Button key={option.value} id={`${id}-dictionary-result-${index}`}
      type="button" variant="outline" className="h-auto min-h-8 w-full justify-start whitespace-normal text-left" disabled={disabled}
      onClick={() => form.setValue(inputName, option.value, { shouldDirty: true, shouldValidate: true })}>
      <Check aria-hidden="true" className="mr-2 h-4 w-4 shrink-0" />{option.label || option.value}
    </Button>)}</div>}
    {page?.hasMore && <Button id={`${id}-dictionary-next`} type="button" variant="outline" disabled={disabled}
      onClick={() => { if (request) setRequest({ search: request.search, after: page.next }); }}><ChevronRight aria-hidden="true" className="mr-2 h-4 w-4" />Next choices</Button>}
  </div>;
};
